import { stringToNumberConverter, usdCurrency } from './formatters';
import { getAge, isDateValid } from './time';
import * as Yup from 'yup';
import { minBuyValue } from 'stash/constants/transactionAmounts';
import { isProd } from './index';

export const yupRequiredBuilder = (partialMessage) => {
	return partialMessage
		? `Please enter ${partialMessage}.`
		: 'Please enter a valid value.';
};

export const required = (name) => (value) => {
	return value ? undefined : `Please enter ${name || 'this field'}.`;
};

export const frequency = required('your frequency');

// Yup Validations
export const birthDate = yupRequiredBuilder('a valid birth day');
export const birthYear = yupRequiredBuilder('a valid birth year');
export const enterEmail = yupRequiredBuilder('your email');
export const vaildEmail = yupRequiredBuilder('a valid email');
export const yupName = yupRequiredBuilder('your name');
export const yupMonth = yupRequiredBuilder('your birth month');

export const containsAlphabeticCharacters = (value) => {
	return value && !/[a-z]/i.test(value)
		? `Should contain alphabetic characters.`
		: undefined;
};

export const hasNoInjectionScript = (value) => {
	return value && /(http|https|<script|<\/script)/.test(value)
		? 'Please enter valid information.'
		: undefined;
};

export const hasNoEmailDomain = (value) => {
	return value && /(@hotmail|@gmail|@yahoo|@outlook)/i.test(value)
		? 'This must not contain an email domain.'
		: undefined;
};

export const hasNoDomainName = (value) => {
	return value && /\.(com|org|net|gov|edu)/i.test(value)
		? 'Please enter valid information.'
		: undefined;
};

export const hasNoIsolatedBlocks = (value) => {
	return value && /(\bidk\b|\bwhy\b|\byes\b|\bno\b)/i.test(value)
		? 'Please enter valid information.'
		: undefined;
};

export const minYear = 1900;

export const maxYear = new Date().getFullYear();

export const yupYearValidation = () => {
	return Yup.number()
		.min(minYear, birthYear)
		.max(maxYear, birthYear)
		.integer(birthDate)
		.required(birthYear);
};

export const yupDateValidation = () => {
	return Yup.number()
		.min(1, birthDate)
		.max(31, birthDate)
		.integer(birthDate)
		.required(birthDate);
};

export const yupMonthValidation = () => {
	return Yup.number().required(yupMonth);
};

export const yupEmailValidation = () => {
	return Yup.string().email(vaildEmail).required(enterEmail);
};

export const yupPasswordValidation = () => {
	return Yup.string()
		.required('A new password is required.')
		.matches(/[A-Z]/, 'Must contain an uppercase character.')
		.matches(/[a-z]/, 'Must contain an lowercase character.')
		.matches(/[0-9]/, 'Must contain a number.')
		.min(8)
		.label('Your new password');
};

export const yupNameValidation = () => {
	return Yup.string().required(yupName);
};

export const yupContainsAlphabeticCharacters = (value) => {
	return value && /[a-z]/i.test(value);
};

export const yupSymbolValidation = (value) => {
	return value && !/[@#$%^&*=+_?!{}\\)()<>]/i.test(value);
};

export const yupHasNoDomainName = (value) => {
	return value && !/\.(com|org|net|gov|edu)/i.test(value);
};

export const yupMustContainNumbersOnly = (value) => {
	return !value || /^\d+$/.test(value);
};

export const yupMinLength = (value, length) => {
	return !value || value.length >= length;
};

// These functions require the schema context to reference full date
// ============================================================ //
const toDoubleDigits = function (day) {
	let singleDigit;

	if (/^\d$/.test(day)) {
		singleDigit = '0' + day;
	}

	return singleDigit || day;
};

export const yupDateIsValid = function (value) {
	const { birth_month, birth_day } = this.parent;

	return isDateValid(new Date(`${value}-${birth_month}-${toDoubleDigits(birth_day)}`));
};

export const yupMustBe18 = function (value) {
	const { birth_month, birth_day } = this.parent;

	const age = getAge(new Date(`${value}-${birth_month}-${toDoubleDigits(birth_day)}`));
	return age >= 18;
};

export const yupYoungerThan113 = function (value) {
	const { birth_month, birth_day } = this.parent;

	const age = getAge(new Date(`${value}-${birth_month}-${toDoubleDigits(birth_day)}`));
	return age < 113;
};

// ============================================================ //
export const yupMustBeMinor = function (value) {
	const { birth_month, birth_day } = this.parent;
	const age = getAge(new Date(`${value}-${birth_month}-${birth_day}`));

	return age >= 0 && age < 18;
};

export const yupDoesNotContainPOBox = function (value) {
	return value && !/p[.\s]*o[.\s]*box/i.test(value);
};

export const yupZipcodeValidation = function (value) {
	return value && /^\d{5}(-\d{4})?$/.test(value);
};

export const yupSSNValidation = function (value) {
	//normalize the input to handle new SDS, space instead of hyphen
	value = value && value.trim().replace(/ /g, '-');
	/*
	 * In order to align with the monolith's checks we need to test
	 * format
	 * area_number
	 * group_number
	 * serial_number
	 * repeating_numbers
	 */
	const validateFormat = () => /^\d{3}-\d{2}-\d{4}$/.test(value);
	const validateAreaNumber = () => {
		/* Check that the area number is not between 900 - 999 in prod
     and 900 - 990 / 993 - 999 in other environment (991 and 992 are used
     for QA purposes in lower environments).*/
		const areaNumber = parseInt(value.split('-')[0]);
		const not000or600 = areaNumber !== 0 && areaNumber !== 666;
		if (isProd()) {
			return not000or600 && (areaNumber < 900 || areaNumber > 999);
		} else {
			return (
				not000or600 &&
				(areaNumber < 900 || areaNumber === 991 || areaNumber === 992 || areaNumber > 999)
			);
		}
	};

	const validateGroupNumber = () => {
		//Validates the group numbers which are the middle 2 numbers.
		const groupNumber = value.split('-')[1];
		return groupNumber !== '00';
	};

	const validateSerialNumber = () => {
		//Validates the serial numbers which are the last 4 numbers.
		const serialNumber = value.split('-').pop();
		return serialNumber !== '0000';
	};

	const validateRepeatingNumbers = () => {
		// Validates that the SSN is not a repeating series of numbers, e.g. 222-22-2222.
		const withoutHyphen = value.replace(/-/g, '');
		const squeezed = [...new Set(withoutHyphen.split(''))].join(''); //like Ruby's squeeze
		return squeezed.length > 1;
	};

	return (
		validateFormat() &&
		validateAreaNumber() &&
		validateGroupNumber() &&
		validateSerialNumber() &&
		validateRepeatingNumbers()
	);
};

const sanitizeNumber = (value) => {
	return (value + '').replace(/[^0-9.-]/g, '');
};

export const yupMinBuyAmount = function (value) {
	const number = sanitizeNumber(value);
	return number && parseFloat(number, 10) >= 5;
};

export const yupMaxBuyAmount = function (value) {
	const number = sanitizeNumber(value);
	return number && parseFloat(number, 10) <= 10000;
};

export const yupExactLength = function (value, length) {
	return value && value.length === length;
};

export const address2 = (value) =>
	value
		? undefined
		: 'If you live in an apartment, unit, or condo, enter the number here.';

export const zip = (value) => {
	return value && value.length < 5 ? 'Please enter your ZIP.' : undefined;
};

export const ssn = (value) => {
	return value && value.length < 9 ? 'Please enter your SSN.' : undefined;
};

export const buyMin = (ticker, investmentType) => (value) => {
	const minAmount = minBuyValue(ticker, investmentType);
	const value_float = value && parseFloat(value);

	// Differentiate between empty string (blank field) and $0.
	if (value_float === '') {
		return true;
	}

	return value_float >= minAmount;
};

export const buyMax =
	(max = 10000) =>
	(value) => {
		return value && parseFloat(value) > max
			? `The maximum investment amount is ${usdCurrency(max)}.`
			: undefined;
	};

function shouldShowCents(value) {
	return value % 1 !== 0;
}

export const amountValidationSchema = (min = 0.01, max = 10000) => {
	return Yup.object().shape({
		amount: Yup.string()
			.required('Please enter an amount.')
			.test({
				test: (value = '') => stringToNumberConverter(value) >= min,
				message: `The minimum is ${usdCurrency(min, shouldShowCents(min))}.`,
			})
			.test({
				test: (value = '') => stringToNumberConverter(value) <= max,
				message: `The max is ${usdCurrency(max, shouldShowCents(max))}.`,
			}),
	});
};
